"use client";
import { styled } from "@mui/material/styles";
import { HomeHeroSection } from "@portal/app/_components/home/HomeHeroSection";
import { StickyFindMyMatchCta } from "@portal/app/_components/home/StickyFindMyMatchCta";
import {
  DreamSearchPreviewCards,
  HomeDreamSearchSection,
} from "@portal/app/_components/home/HomeDreamSearchSection";
import { HomeStatsSection } from "@portal/app/_components/home/HomeStatsSection";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { preload } from "react-dom";
import throttle from "lodash/throttle";
import { HomeIndustrySearchSection } from "@portal/app/_components/home/HomeIndustrySearchSection";
import { HomeFindAClassSection } from "@portal/app/_components/home/HomeFindAClassSection";
import { HomeTestimonialCarouselSection } from "@portal/app/_components/home/HomeTestimonialCarouselSection";
import { HomeFeaturedOnSection } from "@portal/app/_components/home/HomeFeaturedOnSection";
import { HomeFAQSection } from "@portal/app/_components/home/HomeFAQSection";

import { affiliateIndustries } from "@portal/app/_components/home/HomeSharedElements";
import { Layout } from "@portal/app/_components/ui-elements/Layout";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { CustomerNavBar } from "@portal/app/_components/ui-elements/CustomerNavBar";
import { useAffiliateLocalStorage } from "@portal/app/_components/hooks/useAffiliateLocalStorage";
import { HomePageProps } from "./page";

import { AffiliatePageParams } from "./page/[affiliateSlug]/[industry]/page";
import { useParams } from "next/navigation";
import Typography, { TypographyProps } from "@mui/material/Typography";
import NextLink from "next/link";
import { getUrls } from "@portal/app/_common/lib/urls";
import { paletteOptions } from "./_components/theme/options/palette";
import Button from "@mui/material/Button";
import { usePageContext } from "./_components/context/PageContext";
import { DreamSearchSkeletonItems } from "./health/_components/components/DreamSearchResult/DreamSearchSkeletonItem";
import { FALLBACK_ZIP } from "@portal/app/_common/lib/requestUtils";

export const HomePage = ({
  variables,
  industry,
  isAffiliate,
  initialGeoCenter,
  shouldPromptForLocation = false,
  industryVocations,
}: HomePageProps) => {
  // CSS background images are only discovered after styles are applied; preload
  // gets the hero texture fetching from the initial HTML instead.
  preload("/motion-blur.webp", { as: "image" });
  const analytics = useMixpanel();
  useAffiliateLocalStorage();
  const [transparentHeader, setTransparentHeader] = useState(true);
  const { clientLocation, hasExperimentValue } = usePageContext();
  const [zip, setZip] = useState(initialGeoCenter.zip ?? clientLocation.zip ?? FALLBACK_ZIP);
  const params = useParams<AffiliatePageParams>();
  const urls = getUrls();

  const headerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const dreamSearchRef = useRef<HTMLDivElement>(null);

  // The DreamSearch query is the heaviest work on initial load, and the cards
  // sit below the fold for most visitors. Defer mounting the section (and thus
  // the query) until the cards are genuinely on screen or the user starts
  // scrolling — whichever happens first. The flag is resolved server-side, so
  // this is correct on the first render and the disabled path server-renders
  // the section exactly as it did before the deferral.
  const deferDreamSearch = hasExperimentValue("HOMEPAGE_DEFERRED_DREAM_SEARCH", "Enabled");
  const [showDreamSearch, setShowDreamSearch] = useState(!deferDreamSearch);
  useEffect(() => {
    if (showDreamSearch) return;
    const target = dreamSearchRef.current;
    if (!target) return;
    const load = () => setShowDreamSearch(true);
    // Catch scrolls that happened before hydration attached the listener below.
    if (window.scrollY > 0) {
      load();
      return;
    }
    // Default rootMargin (0px): if any part of the grid is on screen at load,
    // the cards are visible and should fetch right away. An outward margin
    // would instead fire while they are still below the fold, which on shorter
    // viewports means nearly every page load.
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) load();
    });
    observer.observe(target);
    window.addEventListener("scroll", load, { passive: true, once: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", load);
    };
  }, [showDreamSearch]);

  const industryPills = affiliateIndustries.map((item) => (
    <NextLink
      key={item.industry}
      href={urls.DreamSearch({
        industry: item.industry,
        zip,
        shouldPromptForLocation,
      })}
    >
      <IndustryPill
        className={item.industry === industry ? "selected" : ""}
        onClick={() =>
          analytics.track("Homepage - Navigate to DreamSearch", {
            industry: item.industry,
            zip,
          })
        }
        variant="outlined"
      >
        {item.label}
      </IndustryPill>
    </NextLink>
  ));

  const scrollListener = useMemo(
    () =>
      throttle(() => {
        const scrollingElement = headerRef.current?.ownerDocument.scrollingElement;
        const bodyPosition = bodyRef.current?.offsetTop;
        if (scrollingElement && bodyPosition) {
          setTransparentHeader(scrollingElement.scrollTop <= bodyPosition);
        }
      }, 100),
    []
  );

  useEffect(() => {
    void analytics.trackOnePageView("Homepage - Viewed", {
      isAffiliate,
      industry,
      affiliateSlug: params?.affiliateSlug,
    });

    window.addEventListener("scroll", scrollListener, { passive: true });
    return () => {
      window.removeEventListener("scroll", scrollListener);
    };
  }, [analytics, scrollListener, isAffiliate, params?.affiliateSlug, industry]);

  return (
    <Layout
      footer="BOTH"
      nav={
        <StickyNav>
          <CustomerNavBar
            ref={headerRef}
            isOnDarkBackground={false}
            withTransparentBackground={transparentHeader}
            withHorizontalConstraint
            shouldHideSearch
          />
        </StickyNav>
      }
    >
      <Page>
        <HeroWrapper>
          <HomeHeroSection zip={zip} shouldPromptForLocation={shouldPromptForLocation} />
        </HeroWrapper>
        <div ref={bodyRef} />
        <DreamSearchWrapper>
          <HomepageHeaderTypography className="title" sx={{ padding: "3rem 0 1.5rem" }}>
            Programs near you
          </HomepageHeaderTypography>
          <IndustryPillsDesktop role="navigation" aria-label="Filter by industry">{industryPills}</IndustryPillsDesktop>
          <IndustryPillsMobile role="navigation" aria-label="Filter by industry">{industryPills}</IndustryPillsMobile>
          <DeferredSectionContainer ref={dreamSearchRef}>
            {showDreamSearch ? (
              <Suspense fallback={<DreamSearchSkeletonSection />}>
                <HomeDreamSearchSection
                  variables={variables}
                  industry={industry}
                  setZip={setZip}
                  shouldPromptForLocation={shouldPromptForLocation}
                />
              </Suspense>
            ) : (
              <DreamSearchSkeletonSection />
            )}
          </DeferredSectionContainer>
        </DreamSearchWrapper>
        <HomeFeaturedOnSection />
        <FullWidthWrapper>
          <HomeFindAClassSection />
        </FullWidthWrapper>
        <HomeTestimonialCarouselSection />
        <IndustryGradientWrapper>
          <HomeIndustrySearchSection
            zip={zip}
            shouldPromptForLocation={shouldPromptForLocation}
            industryVocations={industryVocations}
          />
        </IndustryGradientWrapper>
        <FullWidthWrapper>
          <HomeStatsSection />
        </FullWidthWrapper>
        <HomeFAQSection />
      </Page>
      <StickyFindMyMatchCta zip={zip} />
    </Layout>
  );
};

const DreamSearchSkeletonSection = () => {
  return (
    <section>
      <DreamSearchPreviewCards>
        <DreamSearchSkeletonItems itemCount={6} />
      </DreamSearchPreviewCards>
    </section>
  );
};

const Page = styled("main")`
  position: relative;
  height: 100%;
  width: 100%;
  overflow-x: hidden;

  background: white;

  display: flex;
  flex-direction: column;
  align-items: center;

  section {
    width: 100%;
    // standard section width is 75rem, including 1rem padding-left & right.
    // though some sections are 77rem, including 1rem padding-left & right.
    max-width: 75rem;
  }
`;

const StickyNav = styled("div")`
  .dream_search_nav_bar {
    position: fixed;
    width: 100%;
    z-index: 3;
  }
`;

const FullWidthWrapper = styled("div")`
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  display: flex;
  flex-direction: column;
  align-items: center;

  section {
    max-width: 100%;
  }
`;

const HeroWrapper = styled("div")`
  width: 100%;
  /* Fallback matches the image's dominant color to avoid a white flash while it loads */
  background: #d8e8f8 url("/motion-blur.webp") no-repeat center center;
  background-size: cover;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

// Mirrors DreamSearchWrapper's layout so the skeleton/cards keep their width
// while wrapped in the visibility-tracking container.
const DeferredSectionContainer = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DreamSearchWrapper = styled("div")`
  width: 100%;
  background: ${paletteOptions.dream.grayLight};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 3rem;
  margin-top: -1rem;
  padding-top: 1rem;
  position: relative;
  z-index: 2;

  section {
    max-width: 75rem;
  }
`;

const IndustryGradientWrapper = styled("div")`
  width: 100%;
  background: linear-gradient(180deg, #ffffff 0%, #e8f2ff 50%, #ffffff 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 3rem;
`;

export const HomepageHeaderTypography = (props: TypographyProps) => (
  <Typography
    variant="new_h2"
    {...props}
    sx={{
      margin: 0,
      textAlign: "center",
      color: "inherit",
      fontSize: "2.25rem",
      textTransform: "uppercase",
      ...props.sx,
    }}
  />
);

const IndustryPillsDesktop = styled("div")`
  display: flex;
  gap: 0.5rem;

  ${({ theme }) => theme.breakpoints.down("md")} {
    display: none;
  }
`;
const IndustryPillsMobile = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;

  ${({ theme }) => theme.breakpoints.up("md")} {
    display: none;
  }
`;
const IndustryPill = styled(Button)`
  &.MuiButton-root {
    color: black;
    border-radius: 2.5rem;
    height: 2.75rem;
    border: 1px solid ${paletteOptions.dream.borderColor};

    &:hover {
      border: 1px solid ${paletteOptions.primary.main};
      background: ${paletteOptions.dream.grayLight};
    }

    &.selected {
      border: 1px solid ${paletteOptions.primary.main};
      background: ${paletteOptions.dream.grayLight};
    }

    &:focus-visible {
      outline: 2px solid ${paletteOptions.primary.main};
      outline-offset: 2px;
    }
  }
`;
